import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingAPI } from '../../api';
import SlideOver from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import {
  FileText,
  User,
  Building2,
  Calendar,
  CreditCard,
  ArrowRightLeft,
  AlertTriangle,
  Clock,
  Receipt,
  Banknote,
  ExternalLink,
  XCircle,
  Edit,
  Trash2,
  Loader2,
} from 'lucide-react';

/**
 * TransactionDetailSlideOver - Displays full transaction details with linked entities
 * 
 * Props:
 * - open: boolean - Controls visibility
 * - onOpenChange: (open: boolean) => void - Callback for open state changes
 * - transactionId: string | null - The transaction ID to display
 * - onEdit: (transaction) => void - Callback when edit is requested (for manual transactions)
 * - onDelete: (transaction) => void - Callback when delete is requested
 * - onVoid: (transaction) => void - Callback when void is requested
 */
const TransactionDetailSlideOver = ({
  open,
  onOpenChange,
  transactionId,
  onEdit,
  onDelete,
  onVoid,
}) => {
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch transaction details when transactionId changes
  useEffect(() => {
    const fetchTransaction = async () => {
      if (!transactionId || !open) return;
      
      try {
        setLoading(true);
        setError('');
        const data = await bankingAPI.getTransaction(transactionId);
        setTransaction(data);
      } catch (err) {
        setError(err.message || 'Failed to load transaction details');
        console.error('Error fetching transaction:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [transactionId, open]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setTransaction(null);
      setError('');
    }
  }, [open]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer: 'Transfer',
      manual: 'Manual',
    };
    return labels[type] || type;
  };

  const getTransactionTypeBadge = (type) => {
    const variants = {
      deposit: 'success',
      withdrawal: 'danger',
      transfer: 'info',
      manual: 'neutral',
    };
    return variants[type] || 'neutral';
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'neutral',
      sent: 'info',
      'part-paid': 'warning',
      'paid-in-full': 'success',
      overdue: 'danger',
      unpaid: 'danger',
      paid: 'success',
    };
    return variants[status] || 'neutral';
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      bank_transfer: 'Bank Transfer',
      cash: 'Cash',
      cheque: 'Cheque',
      card: 'Card',
      direct_debit: 'Direct Debit',
      standing_order: 'Standing Order',
      other: 'Other',
    };
    return labels[method] || method;
  };

  const isIncoming = transaction?.transaction_type === 'deposit' || 
    (transaction?.transaction_type === 'transfer' && transaction?.linked_transfer);

  // Determine what type of linked entity we have
  const hasInvoicePayment = !!transaction?.linked_invoice_payment;
  const hasPurchasePayment = !!transaction?.linked_purchase_payment;
  const hasTransfer = !!transaction?.linked_transfer;
  const isVoided = transaction?.is_voided;
  const isReversal = !!transaction?.reversal_of;
  const wasReversed = !!transaction?.reversed_by;

  const getTitle = () => {
    if (!transaction) return 'Transaction Details';
    if (isVoided) return 'Voided Transaction';
    if (hasInvoicePayment) return 'Invoice Payment';
    if (hasPurchasePayment) return 'Purchase Payment';
    if (hasTransfer) return 'Transfer';
    if (isReversal) return 'Reversal Transaction';
    return 'Transaction Details';
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title={getTitle()}
      width="w-full max-w-xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-accent-primary)]" />
        </div>
      ) : error ? (
        <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
          {error}
        </div>
      ) : transaction ? (
        <div className="space-y-6">
          {/* Voided Warning Banner */}
          {isVoided && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-[var(--ds-status-danger)] mt-0.5" />
                <div>
                  <h4 className="font-medium text-[var(--ds-status-danger)]">
                    Transaction Voided
                  </h4>
                  <p className="text-sm text-[var(--ds-text-secondary)] mt-1">
                    {transaction.voided_reason || 'This transaction has been voided.'}
                  </p>
                  {transaction.voided_by && (
                    <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                      Voided by {transaction.voided_by.first_name} {transaction.voided_by.last_name} on{' '}
                      {formatDateTime(transaction.voided_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reversal Info Banner */}
          {isReversal && (
            <div className="bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)]/30 rounded-[var(--ds-radius-md)] p-4">
              <div className="flex items-start gap-3">
                <ArrowRightLeft className="h-5 w-5 text-[var(--ds-status-warning)] mt-0.5" />
                <div>
                  <h4 className="font-medium text-[var(--ds-status-warning)]">
                    Reversal Transaction
                  </h4>
                  <p className="text-sm text-[var(--ds-text-secondary)] mt-1">
                    This transaction reverses: {transaction.reversal_of.description || 'Original transaction'}
                  </p>
                  <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                    Original: {formatCurrency(transaction.reversal_of.amount)} on{' '}
                    {formatDate(transaction.reversal_of.transaction_date)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Was Reversed Info Banner */}
          {wasReversed && !isVoided && (
            <div className="bg-[var(--ds-status-info-bg)] border border-[var(--ds-status-info)]/30 rounded-[var(--ds-radius-md)] p-4">
              <div className="flex items-start gap-3">
                <ArrowRightLeft className="h-5 w-5 text-[var(--ds-status-info)] mt-0.5" />
                <div>
                  <h4 className="font-medium text-[var(--ds-status-info)]">
                    Transaction Reversed
                  </h4>
                  <p className="text-sm text-[var(--ds-text-secondary)] mt-1">
                    This transaction has been reversed by another transaction.
                  </p>
                  <p className="text-xs text-[var(--ds-text-muted)] mt-1">
                    Reversal: {formatCurrency(transaction.reversed_by.amount)} on{' '}
                    {formatDate(transaction.reversed_by.transaction_date)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Amount & Type Card */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-[var(--ds-text-muted)] mb-1">Amount</div>
                  <div className={`text-2xl font-bold tabular-nums ${
                    isIncoming 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  } ${isVoided ? 'line-through opacity-60' : ''}`}>
                    {isIncoming ? '+' : '-'}{formatCurrency(transaction.amount)}
                  </div>
                </div>
                <div className="text-right">
                  <Badge 
                    variant={isVoided ? 'neutral' : getTransactionTypeBadge(transaction.transaction_type)}
                    className={isVoided ? 'opacity-60' : ''}
                  >
                    {getTransactionTypeLabel(transaction.transaction_type)}
                  </Badge>
                  {isVoided && (
                    <Badge variant="danger" className="ml-2">
                      Voided
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
              Transaction Details
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Date
                </div>
                <div className="text-sm text-[var(--ds-text-primary)]">
                  {formatDate(transaction.transaction_date)}
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                  <FileText className="h-3.5 w-3.5" />
                  Reference
                </div>
                <div className="text-sm text-[var(--ds-text-primary)]">
                  {transaction.reference || '-'}
                </div>
              </div>
            </div>

            {transaction.description && (
              <div>
                <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                  Description
                </div>
                <div className="text-sm text-[var(--ds-text-primary)]">
                  {transaction.description}
                </div>
              </div>
            )}

            {transaction.notes && (
              <div>
                <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                  Notes
                </div>
                <div className="text-sm text-[var(--ds-text-secondary)] bg-[var(--ds-surface-panel2)] p-3 rounded-md">
                  {transaction.notes}
                </div>
              </div>
            )}
          </div>

          {/* Bank Account */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
              Bank Account
            </h3>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-[var(--ds-accent-primary-bg)] rounded-md">
                    <Banknote className="h-5 w-5 text-[var(--ds-accent-primary)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[var(--ds-text-primary)]">
                      {transaction.bank_account.account_name}
                    </div>
                    <div className="text-sm text-[var(--ds-text-muted)]">
                      {transaction.bank_account.bank_name || transaction.bank_account.account_type}
                    </div>
                    {(transaction.bank_account.sort_code || transaction.bank_account.account_number) && (
                      <div className="text-xs text-[var(--ds-text-muted)] mt-1">
                        {transaction.bank_account.sort_code && `SC: ${transaction.bank_account.sort_code}`}
                        {transaction.bank_account.sort_code && transaction.bank_account.account_number && ' | '}
                        {transaction.bank_account.account_number && `AC: ${transaction.bank_account.account_number}`}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Linked Invoice Payment */}
          {hasInvoicePayment && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
                Invoice Payment
              </h3>
              
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Client Info */}
                  {transaction.linked_invoice_payment.client && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-[var(--ds-status-info-bg)] rounded-md">
                        {transaction.linked_invoice_payment.client.company_name ? (
                          <Building2 className="h-5 w-5 text-[var(--ds-status-info)]" />
                        ) : (
                          <User className="h-5 w-5 text-[var(--ds-status-info)]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-[var(--ds-text-primary)]">
                          {transaction.linked_invoice_payment.client.display_name}
                        </div>
                        {transaction.linked_invoice_payment.client.email && (
                          <div className="text-sm text-[var(--ds-text-muted)]">
                            {transaction.linked_invoice_payment.client.email}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Invoice Details */}
                  <div className="border-t border-[var(--ds-border-divider)] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-[var(--ds-text-muted)]" />
                        <span className="font-medium text-[var(--ds-text-primary)]">
                          Invoice #{transaction.linked_invoice_payment.invoice.invoice_num}
                        </span>
                      </div>
                      <Badge variant={getStatusBadge(transaction.linked_invoice_payment.invoice.status)}>
                        {transaction.linked_invoice_payment.invoice.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Invoice Total:</span>
                        <span className="ml-2 text-[var(--ds-text-primary)] font-medium tabular-nums">
                          {formatCurrency(transaction.linked_invoice_payment.invoice.total)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Amount Paid:</span>
                        <span className="ml-2 text-green-600 dark:text-green-400 font-medium tabular-nums">
                          {formatCurrency(transaction.linked_invoice_payment.invoice.amount_paid)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Balance Due:</span>
                        <span className={`ml-2 font-medium tabular-nums ${
                          transaction.linked_invoice_payment.invoice.balance_due > 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-600 dark:text-green-400'
                        }`}>
                          {formatCurrency(transaction.linked_invoice_payment.invoice.balance_due)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Payment:</span>
                        <span className="ml-2 text-[var(--ds-text-primary)]">
                          {getPaymentMethodLabel(transaction.linked_invoice_payment.payment_method)}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() => navigate(`/tenant/invoices/${transaction.linked_invoice_payment.invoice.id}`)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Linked Purchase Payment */}
          {hasPurchasePayment && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
                Purchase Payment
              </h3>
              
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Supplier Info */}
                  {transaction.linked_purchase_payment.supplier && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-[var(--ds-status-warning-bg)] rounded-md">
                        <Building2 className="h-5 w-5 text-[var(--ds-status-warning)]" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-[var(--ds-text-primary)]">
                          {transaction.linked_purchase_payment.supplier.company_name || 
                           transaction.linked_purchase_payment.supplier.contact_name}
                        </div>
                        {transaction.linked_purchase_payment.supplier.email && (
                          <div className="text-sm text-[var(--ds-text-muted)]">
                            {transaction.linked_purchase_payment.supplier.email}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Purchase Details */}
                  <div className="border-t border-[var(--ds-border-divider)] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-[var(--ds-text-muted)]" />
                        <span className="font-medium text-[var(--ds-text-primary)]">
                          Purchase #{transaction.linked_purchase_payment.purchase.purchase_num}
                        </span>
                      </div>
                      <Badge variant={getStatusBadge(transaction.linked_purchase_payment.purchase.status)}>
                        {transaction.linked_purchase_payment.purchase.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Purchase Total:</span>
                        <span className="ml-2 text-[var(--ds-text-primary)] font-medium tabular-nums">
                          {formatCurrency(transaction.linked_purchase_payment.purchase.total)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Amount Paid:</span>
                        <span className="ml-2 text-green-600 dark:text-green-400 font-medium tabular-nums">
                          {formatCurrency(transaction.linked_purchase_payment.purchase.amount_paid)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Balance Due:</span>
                        <span className={`ml-2 font-medium tabular-nums ${
                          transaction.linked_purchase_payment.purchase.balance_due > 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-600 dark:text-green-400'
                        }`}>
                          {formatCurrency(transaction.linked_purchase_payment.purchase.balance_due)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--ds-text-muted)]">Payment:</span>
                        <span className="ml-2 text-[var(--ds-text-primary)]">
                          {getPaymentMethodLabel(transaction.linked_purchase_payment.payment_method)}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() => navigate(`/tenant/purchases/${transaction.linked_purchase_payment.purchase.id}`)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Purchase
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Linked Transfer */}
          {hasTransfer && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
                Transfer Details
              </h3>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[var(--ds-status-info-bg)] rounded-md">
                      <ArrowRightLeft className="h-5 w-5 text-[var(--ds-status-info)]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-[var(--ds-text-muted)]">
                        {isIncoming ? 'Transfer from' : 'Transfer to'}
                      </div>
                      <div className="font-medium text-[var(--ds-text-primary)]">
                        {transaction.linked_transfer.bank_account.account_name}
                      </div>
                      <div className="text-sm text-[var(--ds-text-muted)]">
                        {transaction.linked_transfer.bank_account.account_type}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-medium tabular-nums ${
                        isIncoming 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {isIncoming ? '-' : '+'}{formatCurrency(transaction.linked_transfer.amount)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Audit Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--ds-text-primary)] uppercase tracking-wide">
              Audit Info
            </h3>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                  <Clock className="h-3.5 w-3.5" />
                  Created
                </div>
                <div className="text-[var(--ds-text-primary)]">
                  {formatDateTime(transaction.created_at)}
                </div>
              </div>
              
              {transaction.updated_at && transaction.updated_at !== transaction.created_at && (
                <div>
                  <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                    <Clock className="h-3.5 w-3.5" />
                    Updated
                  </div>
                  <div className="text-[var(--ds-text-primary)]">
                    {formatDateTime(transaction.updated_at)}
                  </div>
                </div>
              )}

              {transaction.processed_by && (
                <div className="col-span-2">
                  <div className="flex items-center gap-2 text-xs text-[var(--ds-text-muted)] mb-1">
                    <User className="h-3.5 w-3.5" />
                    Processed By
                  </div>
                  <div className="text-[var(--ds-text-primary)]">
                    {transaction.processed_by.first_name} {transaction.processed_by.last_name}
                    <span className="text-[var(--ds-text-muted)] ml-2">
                      ({transaction.processed_by.email})
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {!isVoided && !isReversal && !wasReversed && (
            <div className="border-t border-[var(--ds-border-divider)] pt-6 space-y-3">
              {/* Manual transaction actions */}
              {transaction.is_editable && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => onEdit?.(transaction)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Transaction
                </Button>
              )}

              {/* Delete/Remove actions */}
              {transaction.is_deletable && (
                <>
                  {hasInvoicePayment || hasPurchasePayment ? (
                    <Button
                      variant="secondary"
                      className="w-full text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger-bg)]"
                      onClick={() => onDelete?.(transaction)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Payment
                    </Button>
                  ) : hasTransfer ? (
                    <Button
                      variant="secondary"
                      className="w-full text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger-bg)]"
                      onClick={() => onDelete?.(transaction)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Transfer
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      className="w-full text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger-bg)]"
                      onClick={() => onDelete?.(transaction)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Transaction
                    </Button>
                  )}
                </>
              )}

              {/* Void action for linked transactions */}
              {(hasInvoicePayment || hasPurchasePayment) && onVoid && (
                <Button
                  variant="secondary"
                  className="w-full text-[var(--ds-status-warning)] hover:bg-[var(--ds-status-warning-bg)]"
                  onClick={() => onVoid?.(transaction)}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Void Transaction
                </Button>
              )}
            </div>
          )}

          {/* Voided transaction - no actions */}
          {isVoided && (
            <div className="border-t border-[var(--ds-border-divider)] pt-6">
              <p className="text-sm text-[var(--ds-text-muted)] text-center">
                No actions available for voided transactions.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </SlideOver>
  );
};

export default TransactionDetailSlideOver;
